import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const utilityActionSelector = ".sale-header__button, .receiving-launch, .stock-runtime-launch, .sales-history-launch, .cash-out-launch";
const compactMenuMedia = "(min-width: 1041px)";

type UtilityActionKind =
  | "switch-user"
  | "receiving"
  | "receiving-history"
  | "stock"
  | "sales-history"
  | "cash-out"
  | "shift-close"
  | "logout"
  | "other";

type UtilityActionGroup = "work" | "stock-data" | "cash-shift" | "other";

type UtilityActionPresentation = {
  kind: UtilityActionKind;
  group: UtilityActionGroup;
  label: string;
  description: string;
};

function sourceActionLabel(action: HTMLButtonElement) {
  return (action.textContent ?? "").replace(/\s+/g, " ").trim() || "งาน POS";
}

function actionPresentation(action: HTMLButtonElement): UtilityActionPresentation {
  if (action.classList.contains("sale-header__button--quiet")) {
    return { kind: "logout", group: "other", label: "ออกจากระบบ", description: "สิ้นสุดการใช้งานบนเครื่องนี้" };
  }
  if (action.classList.contains("shift-close-launch")) {
    return {
      kind: "shift-close",
      group: "cash-shift",
      label: sourceActionLabel(action),
      description: "สรุปยอดและปิดการทำงานประจำวัน",
    };
  }
  if (action.classList.contains("receiving-launch--history")) {
    return { kind: "receiving-history", group: "stock-data", label: "ประวัติรับ", description: "ดูรายการรับสินค้าย้อนหลัง" };
  }
  if (action.classList.contains("receiving-launch")) {
    return { kind: "receiving", group: "work", label: "รับสินค้า", description: "บันทึกรับสินค้าและตรวจนับ" };
  }
  if (action.classList.contains("stock-runtime-launch")) {
    return { kind: "stock", group: "stock-data", label: "สต็อก", description: "ตรวจสอบยอดคงเหลือ" };
  }
  if (action.classList.contains("sales-history-launch")) {
    return { kind: "sales-history", group: "stock-data", label: "ประวัติการขาย", description: "ดูรายการขายและข้อมูลย้อนหลัง" };
  }
  if (action.classList.contains("cash-out-launch")) {
    return { kind: "cash-out", group: "cash-shift", label: "เงินออกจากลิ้นชัก", description: "บันทึกการนำเงินออก" };
  }
  if (action.classList.contains("sale-header__button")) {
    return { kind: "switch-user", group: "work", label: "สลับผู้ใช้งาน", description: "เปลี่ยนผู้ใช้งานบนเครื่องนี้" };
  }
  return { kind: "other", group: "other", label: sourceActionLabel(action), description: "งาน POS" };
}

function ActionIcon({ kind }: { kind: UtilityActionKind }) {
  const common = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
    "aria-hidden": true,
  };

  if (kind === "switch-user") {
    return <svg {...common}><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M16 8h6m-3-3 3 3-3 3"/></svg>;
  }
  if (kind === "receiving") {
    return <svg {...common}><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/><path d="m3.3 7 8.7 5 8.7-5M12 22V12"/><path d="M16 4.3 7.5 9.2"/></svg>;
  }
  if (kind === "receiving-history") {
    return <svg {...common}><path d="M3 12a9 9 0 1 0 3-6.7L3 8"/><path d="M3 3v5h5"/><path d="M12 7v5l3 2"/></svg>;
  }
  if (kind === "stock") {
    return <svg {...common}><path d="m21 8-9-5-9 5 9 5 9-5Z"/><path d="m3 8 9 5 9-5M3 12l9 5 9-5M3 16l9 5 9-5"/></svg>;
  }
  if (kind === "sales-history") {
    return <svg {...common}><path d="M4 19V9m5 10V5m5 14v-7m5 7V3"/><path d="M2 21h20"/></svg>;
  }
  if (kind === "cash-out") {
    return <svg {...common}><rect x="3" y="7" width="18" height="12" rx="2"/><path d="M7 7V5h10v2M7 13h10"/><path d="M12 10v6"/></svg>;
  }
  if (kind === "shift-close") {
    return <svg {...common}><path d="M21 12.8A9 9 0 1 1 11.2 3 7 7 0 0 0 21 12.8Z"/></svg>;
  }
  if (kind === "logout") {
    return <svg {...common}><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><path d="m16 17 5-5-5-5M21 12H9"/></svg>;
  }
  return <svg {...common}><circle cx="12" cy="12" r="9"/><path d="M12 8v4m0 4h.01"/></svg>;
}

const groupOrder: Array<{ key: UtilityActionGroup; label: string }> = [
  { key: "work", label: "การทำงาน" },
  { key: "stock-data", label: "สต็อกและข้อมูล" },
  { key: "cash-shift", label: "เงินสดและกะ" },
  { key: "other", label: "อื่น ๆ" },
];

export function SaleUtilityMenuHost() {
  const [toggleMountNode, setToggleMountNode] = useState<Element | null>(null);
  const [popoverMountNode, setPopoverMountNode] = useState<Element | null>(null);
  const [actions, setActions] = useState<HTMLButtonElement[]>([]);
  const [compactMenu, setCompactMenu] = useState(() => window.matchMedia(compactMenuMedia).matches);
  const [open, setOpen] = useState(false);
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const media = window.matchMedia(compactMenuMedia);
    const updateMedia = () => {
      const nextCompact = media.matches;
      setCompactMenu(nextCompact);
      if (!nextCompact) setOpen(false);
    };
    updateMedia();
    media.addEventListener("change", updateMedia);
    return () => media.removeEventListener("change", updateMedia);
  }, []);

  useEffect(() => {
    const updateRuntimeNodes = () => {
      const actionSource = document.querySelector(".sale-header__actions");
      setToggleMountNode(document.querySelector(".sale-category-strip"));
      setPopoverMountNode(document.querySelector(".sale-workspace"));
      setActions(
        actionSource
          ? Array.from(actionSource.querySelectorAll<HTMLButtonElement>(utilityActionSelector))
          : [],
      );
    };
    updateRuntimeNodes();
    const observer = new MutationObserver(updateRuntimeNodes);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["disabled"],
    });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!open) return;
    const closeOnOutside = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (toggleRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setOpen(false);
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", closeOnOutside);
    document.addEventListener("keydown", closeOnEscape);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutside);
      document.removeEventListener("keydown", closeOnEscape);
    };
  }, [open]);

  if (!toggleMountNode || !popoverMountNode || !compactMenu) return null;

  const entries = actions.map((action) => ({ action, presentation: actionPresentation(action) }));
  const logoutEntry = entries.find((entry) => entry.presentation.kind === "logout") ?? null;
  const groupedEntries = entries.filter((entry) => entry.presentation.kind !== "logout");

  const invoke = (action: HTMLButtonElement) => {
    if (action.disabled || !action.isConnected) return;
    setOpen(false);
    action.click();
  };

  const renderAction = ({ action, presentation }: (typeof entries)[number]) => (
    <button
      key={`${presentation.kind}:${action.className}`}
      type="button"
      className="sale-utility-action"
      disabled={action.disabled}
      onClick={() => invoke(action)}
    >
      <span className="sale-utility-action__icon"><ActionIcon kind={presentation.kind} /></span>
      <span className="sale-utility-action__copy">
        <strong>{presentation.label}</strong>
        <small>{presentation.description}</small>
      </span>
      <span className="sale-utility-action__chevron" aria-hidden="true">›</span>
    </button>
  );

  return (
    <>
      {createPortal(
        <button
          ref={toggleRef}
          className="sale-utility-toggle"
          type="button"
          aria-expanded={open}
          aria-controls="sale-utility-popover"
          aria-label="เปิดเมนู POS"
          onClick={() => setOpen((current) => !current)}
        >
          <span aria-hidden="true">☰</span>
          <strong>เมนู</strong>
        </button>,
        toggleMountNode,
      )}
      {createPortal(
        <aside
          ref={popoverRef}
          id="sale-utility-popover"
          className="sale-utility-popover"
          aria-hidden={!open}
          aria-label="เมนู POS"
          style={open ? { display: "flex" } : undefined}
        >
          <div className="sale-utility-popover__header">
            <div className="sale-utility-brand" aria-label="MORE POS">
              <span className="sale-utility-brand__mark">M</span>
              <span><strong>MORE POS</strong><small>Point of Sale</small></span>
            </div>
            <button className="sale-utility-close" type="button" aria-label="ปิดเมนู" onClick={() => setOpen(false)}>×</button>
          </div>

          <div className="sale-utility-popover__body">
            {groupOrder.map((group) => {
              const groupEntries = groupedEntries.filter((entry) => entry.presentation.group === group.key);
              if (!groupEntries.length) return null;
              return (
                <section className="sale-utility-group" key={group.key} aria-label={group.label}>
                  <p>{group.label}</p>
                  <div>{groupEntries.map(renderAction)}</div>
                </section>
              );
            })}
            {!groupedEntries.length && !logoutEntry ? (
              <div className="sale-utility-empty">ยังไม่มีงานที่ใช้ได้ในขอบเขตนี้</div>
            ) : null}
          </div>

          {logoutEntry ? (
            <div className="sale-utility-popover__footer">
              <button
                type="button"
                className="sale-utility-logout"
                disabled={logoutEntry.action.disabled}
                onClick={() => invoke(logoutEntry.action)}
              >
                <span className="sale-utility-action__icon"><ActionIcon kind="logout" /></span>
                <span className="sale-utility-action__copy">
                  <strong>{logoutEntry.presentation.label}</strong>
                  <small>{logoutEntry.presentation.description}</small>
                </span>
                <span className="sale-utility-action__chevron" aria-hidden="true">›</span>
              </button>
            </div>
          ) : null}
        </aside>,
        popoverMountNode,
      )}
    </>
  );
}
